import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Pagination,
  CircularProgress,
  Alert,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import pandoraAdminService, {
  PandoraDocument,
  DocumentListResponse,
} from '../../../services/pandoraAdminService';

const STATUS_COLORS: Record<
  string,
  'default' | 'info' | 'warning' | 'success' | 'error'
> = {
  uploaded: 'default',
  ingested: 'info',
  classified: 'info',
  segmented: 'warning',
  validated: 'success',
  promoted: 'success',
  rejected: 'error',
  error: 'error',
};

const CATEGORY_LABELS: Record<string, string> = {
  petition: 'Petição',
  law: 'Legislação',
  legal_case: 'Jurisprudência',
  legal_theory: 'Doutrina',
  verbetes: 'Verbetes',
};

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'uploaded', label: 'Uploaded' },
  { value: 'ingested', label: 'Ingested' },
  { value: 'classified', label: 'Classified' },
  { value: 'segmented', label: 'Segmented' },
  { value: 'validated', label: 'Validated' },
  { value: 'promoted', label: 'Promoted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'error', label: 'Error' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'petition', label: 'Petição' },
  { value: 'law', label: 'Legislação' },
  { value: 'legal_case', label: 'Jurisprudência' },
  { value: 'legal_theory', label: 'Doutrina' },
  { value: 'verbetes', label: 'Verbetes' },
];

function getStatusColor(
  status: string
): 'default' | 'info' | 'warning' | 'success' | 'error' {
  const key = status.toLowerCase();
  return STATUS_COLORS[key] ?? 'default';
}

function getCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

function formatDate(iso?: string): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR');
  } catch {
    return '-';
  }
}

export default function PandoraDocumentsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<DocumentListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; doc: PandoraDocument | null }>({
    open: false,
    doc: null,
  });

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: {
        search?: string;
        status?: string;
        category?: string;
        page?: number;
        page_size?: number;
      } = {
        page,
        page_size: pageSize,
      };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const res = await pandoraAdminService.listDocuments(params);
      setData(res);
    } catch (err) {
      console.error('Erro ao carregar documentos Pandora:', err);
      setError('Não foi possível carregar os documentos.');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, categoryFilter]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleRefresh = () => {
    loadDocuments();
  };

  const handleFilterChange = () => {
    setPage(1);
    loadDocuments();
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!data?.items) return;
    if (event.target.checked) {
      setSelected(new Set(data.items.map((d) => d.id)));
    } else {
      setSelected(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const handleView = (id: string) => {
    navigate(`/admin/pandora/documents/${id}`);
  };

  const handleProcess = async (id: string) => {
    try {
      setProcessingId(id);
      await pandoraAdminService.processDocument(id);
      loadDocuments();
    } catch (err) {
      console.error('Erro ao processar documento:', err);
      setError('Falha ao processar documento.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleBatchProcess = async () => {
    if (selected.size === 0) return;
    try {
      setBatchProcessing(true);
      setError(null);
      await pandoraAdminService.batchProcess(Array.from(selected));
      setSelected(new Set());
      loadDocuments();
    } catch (err) {
      console.error('Erro no processamento em lote:', err);
      setError('Falha no processamento em lote.');
    } finally {
      setBatchProcessing(false);
    }
  };

  const handleDeleteClick = (doc: PandoraDocument) => {
    setDeleteDialog({ open: true, doc });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.doc) return;
    try {
      await pandoraAdminService.deleteDocument(deleteDialog.doc.id);
      setDeleteDialog({ open: false, doc: null });
      loadDocuments();
    } catch (err) {
      console.error('Erro ao excluir documento:', err);
      setError('Falha ao excluir documento.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, doc: null });
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Documentos Pandora</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Atualizar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <TextField
          label="Buscar"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleFilterChange()}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Categoria</InputLabel>
          <Select
            value={categoryFilter}
            label="Categoria"
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(1);
            }}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" size="small" onClick={handleFilterChange}>
          Filtrar
        </Button>
      </Box>

      {someSelected && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              batchProcessing ? <CircularProgress size={18} /> : <PlayArrowIcon />
            }
            onClick={handleBatchProcess}
            disabled={batchProcessing}
          >
            Processar Selecionados ({selected.size})
          </Button>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someSelected && !allSelected}
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Área</TableCell>
              <TableCell>Data</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Nenhum documento encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(doc.id)}
                      onChange={() => handleSelectOne(doc.id)}
                    />
                  </TableCell>
                  <TableCell>{doc.title || doc.filename || '-'}</TableCell>
                  <TableCell>{getCategoryLabel(doc.category)}</TableCell>
                  <TableCell>
                    <Chip
                      label={doc.processing_status}
                      color={getStatusColor(doc.processing_status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{doc.legal_domain || '-'}</TableCell>
                  <TableCell>{formatDate(doc.created_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Visualizar">
                      <IconButton size="small" onClick={() => handleView(doc.id)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Processar">
                      <IconButton
                        size="small"
                        onClick={() => handleProcess(doc.id)}
                        disabled={processingId === doc.id}
                      >
                        {processingId === doc.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <PlayArrowIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(doc)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o documento &quot;
            {deleteDialog.doc?.title || deleteDialog.doc?.filename || deleteDialog.doc?.id}
            &quot;?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
